'use client';

import { Anchor, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { LimitedTextInput, TrackedButton } from '@/components';
import { FIRST_NAME_MAX_LENGTH, LAST_NAME_MAX_LENGTH } from '@/constants';
import { useFooter } from '@/contexts';
import { AuthMachineEvent } from '@/machines';

import styles from './UserDetailsForm.module.css';

interface UserDetailsFormProps {
  send: (event: AuthMachineEvent) => void;
  firstName?: string;
  lastName?: string;
  isLoading?: boolean;
}

interface UserDetailsFormValues {
  firstName?: string;
  lastName?: string;
}

const UserDetailsForm = ({
  send,
  firstName = '',
  lastName = '',
  isLoading = false,
}: UserDetailsFormProps) => {
  const buttonText = useMemo(() => 'COMPLETE REGISTRATION', []);

  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm({
    initialValues: {
      firstName,
      lastName,
    },
    validate: {
      firstName: (value) => (value ? null : 'First name is required'),
      lastName: (value) => (value ? null : 'Last name is required'),
    },
  });

  const handleSubmit = useCallback(
    async (values: UserDetailsFormValues) => {
      // Update state first
      send({
        type: 'UPDATE_USER_DETAILS',
        firstName: values.firstName ?? '',
        lastName: values.lastName ?? '',
      });

      // Send completion event
      send({
        type: 'USER_DETAILS_SUBMITTED',
      });
    },
    [send],
  );

  const { buttonPrimaryProps, setButtonPrimaryProps, clearButtonPrimaryProps } =
    useFooter();

  useEffect(() => {
    setButtonPrimaryProps({
      children: buttonText,
      type: 'submit',
      loading: isLoading,
      variant: 'primary',
      trackingName: 'UserDetailsForm_complete_registration',
      onClick: () => formRef?.current?.requestSubmit(),
    });

    return () => clearButtonPrimaryProps();
  }, [
    buttonText,
    clearButtonPrimaryProps,
    handleSubmit,
    isLoading,
    setButtonPrimaryProps,
  ]);

  return (
    <form
      ref={formRef}
      className={styles.form}
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <Stack gap="lg">
        <LimitedTextInput
          value={firstName}
          label="First Name"
          placeholder="Enter your first name"
          required
          {...form.getInputProps('firstName')}
          disabled={isLoading}
          autoFocus
          maxLength={FIRST_NAME_MAX_LENGTH}
        />

        <LimitedTextInput
          value={lastName}
          label="Last Name"
          placeholder="Enter your last name"
          required
          {...form.getInputProps('lastName')}
          disabled={isLoading}
          maxLength={LAST_NAME_MAX_LENGTH}
        />

        <Text
          size="md"
          ta="left"
        >
          {/* TODO: add terms and privacy policy link */}
          By continuing, you accept Sway&apos;s{' '}
          <Anchor href="/terms">Terms</Anchor> and{' '}
          <Anchor href="/privacy">Privacy Policy</Anchor>. We&apos;ll send a
          verification code to your phone. Standard message rates may apply.
        </Text>

        <TrackedButton
          fullWidth
          mt="lg"
          size="lg"
          visibleFrom="md"
          {...buttonPrimaryProps}
        />
      </Stack>
    </form>
  );
};

export { UserDetailsForm };
